import {
  TextField,
  TextInput,
  BooleanInput,
  PasswordInput,
  Link,
  useRecordContext,
  ArrayInput,
  SimpleFormIterator,
  ArrayField,
  Datagrid,
  WithRecord,
} from "react-admin";
import { Box, Chip } from "@mui/material";

import RegenerateSecretButton from "../components/buttonRenerateSecret";
import CopyTextField from "../components/copyTextField";
import CopySecretField from "../components/copySecretField";
import ComputerIcon from "@mui/icons-material/Computer";
import { MyList } from "../ra-lb-tools/components/myList";
import {
  MyEdit as Edit,
  MyCreate as Create,
} from "../ra-lb-tools/components/myMutator";
import { MyBooleanField as BooleanField } from "../components/myBooleanField";
import { RecordSummary, MyShow } from "../ra-lb-tools/components/MyShow";
import { LabeledColumn } from "../ra-lb-tools/components/labeledColumn";
import {
  MyTabbedShowLayout,
  Tab,
} from "../ra-lb-tools/components/myTabbedShowLayout";
import { JsonField } from "../ra-lb-tools/components/jsonField";
import {
  MyReferenceField,
  MyReferenceInput,
  MyReferencesField,
} from "../ra-lb-tools/components/myReference";
import { LockerOpenRequestList } from "./lockerOpenRequest";
import { LockerUsageEventList } from "./billing/lockerUsageEvent";
import { UserField, UserInput } from "./users";
import { OpenLockerTokenList } from "./openLockerTokens";
import { Debug } from "../ra-lb-tools/components/debug";
import { KeycloakLink } from "./misc/keycloakLink";
import { Launch } from "@mui/icons-material";
import { OrganizationInput } from "./billing/organizations";
import { getValidResource } from "../ra-lb-tools/acl/tools";
import { MyCreateButton } from "../ra-lb-tools/components/myCreateButton";

export const ClientIcon = ComputerIcon;

const CreateWebhookButton = () => {
  const client = useRecordContext();

  return (
    <MyCreateButton
      resource={getValidResource("webhooks")}
      state={{ record: { sp_client_id: client.clientId } }}
    />
  );
};

const WebhookLink = ({ hook }) => {
  return (
    <Link
      key={hook.id}
      to={`/${getValidResource("webhooks")}/${hook.name}/show`}
    >
      <Chip label={hook.url} />
    </Link>
  );
};

const ClientWebhooks = ({ source: _source, ...props }) => {
  return (
    <MyReferenceField
      className="ClientWebhooks"
      reference="webhooks"
      source="clientSp.whEndpointId"
      sortable={false}
      render={(hook) => <WebhookLink hook={hook} />}
      emptyReference={<CreateWebhookButton />}
      {...props}
    />
  );
};

export const ClientOrganization = ({ source: _source, ...props }) => (
  <MyReferencesField
    reference="organizations"
    target="serviceAccountId"
    source="clientSp.serviceAccountId"
    noReferences={<Debug>No organization for client</Debug>}
    {...props}
  />
);

export const ClientsList = (props) => {
  const clientsFilters = [
    <TextInput source="name" alwaysOn key={1} />,
    <OrganizationInput
      source="organization_id"
      alwaysOn
      key="organization_id"
    />,
  ];
  const helperText = (
    <Link
      to={"https://docs.harborlockers.com/access_token.html"}
      target="_blank"
      sx={{ display: "flex", marginBottom: "20px" }}
    >
      How to get access tokens?
      <Launch />
    </Link>
  );

  return (
    <MyList
      filters={clientsFilters}
      resource="clients"
      dataGridProps={{
        omit: ["clientSettings", "clientSp"],
      }}
      helperText={helperText}
      {...props}
    >
      <Box source="id">
        <TextField source="id" />
        <Debug>
          <KeycloakLink id="id" entity={"clients"} />
        </Debug>
      </Box>
      <TextField source="name" />
      <TextField source="clientId" />
      <BooleanField source="enabled" />
      <ClientWebhooks source="webhooks" label="Webhooks" sortable={false} />
      <UserField
        source="clientSp.serviceAccountId"
        label="Service Account"
        sortable={false}
      />
      <ClientOrganization source="organization_id" sortable={false} />
      <JsonField source="clientSettings" />
      <JsonField source="clientSp" />
    </MyList>
  );
};

export const ClientsView = (props) => {
  return (
    <MyShow {...props}>
      <WithRecord
        render={(client) => (
          <MyTabbedShowLayout>
            <Tab label="Summary" can={true}>
              <RecordSummary>
                <LabeledColumn>
                  <Box source="id">
                    <TextField source="id" />
                    <Debug>
                      <KeycloakLink id="id" entity={"clients"} />
                    </Debug>
                  </Box>
                  <TextField source="name" />
                  <TextField source="description" />
                  <BooleanField source="enabled" />
                  <UserField
                    source="clientSp.serviceAccountId"
                    label="Service Account"
                  />
                </LabeledColumn>
                <LabeledColumn>
                  <CopyTextField source="clientId" />
                  <CopySecretField source="secret" label="secret" />
                  <ClientWebhooks source="webhooks" label="Webhooks" />
                  <ClientOrganization source="organization_id" />
                </LabeledColumn>
                <LabeledColumn>
                  <ArrayField
                    label="Request Info Parser"
                    source="clientSettings.requestInfoParser"
                  >
                    <Datagrid bulkActionButtons={null}>
                      <TextField source="regex" />
                      <TextField source="template" />
                    </Datagrid>
                  </ArrayField>
                  <JsonField source="clientSp" collapsed={true} />
                </LabeledColumn>
              </RecordSummary>
            </Tab>
            <Tab
              label="Locker Open Request"
              path="locker-open-request"
              can={{ action: "list", resource: "locker-open-requests" }}
            >
              <LockerOpenRequestList filter={{ clientId: client.clientId }} />
            </Tab>
            <Tab
              label="Locker Usage Events"
              path="locker-usage-events"
              can={{ action: "list", resource: "locker-usage-events" }}
            >
              <LockerUsageEventList filter={{ clientId: client.clientId }} />
            </Tab>
            <Tab
              label="Open Locker Tokens"
              path="open-locker-tokens"
              can={{ action: "list", resource: "open-locker-tokens" }}
            >
              <OpenLockerTokenList filter={{ clientId: client.clientId }} />
            </Tab>
          </MyTabbedShowLayout>
        )}
      />
    </MyShow>
  );
};

export const clientsEdit = () => {
  return (
    <Edit>
      <TextInput disabled source="id" />
      <TextInput source="name" />
      <TextInput multiline source="description" />
      <BooleanInput source="enabled" />
      <TextInput disabled source="clientId" />
      <PasswordInput disabled source="secret" />
      <RegenerateSecretButton />
      <ArrayInput
        source="clientSettings.requestInfoParser"
        sx={{ marginTop: "15px" }}
      >
        <SimpleFormIterator inline={true}>
          <TextInput source="regex" />
          <TextInput source="template" />
        </SimpleFormIterator>
      </ArrayInput>
    </Edit>
  );
};

export const clientsCreate = () => {
  return (
    <Create redirect="show">
      <TextInput source="clientId" />
      <TextInput source="name" />
      <TextInput multiline source="description" />
      <UserInput source="ownerId" allowEmpty />
      <ArrayInput source="client_settings.request_info_parser">
        <SimpleFormIterator inline={true}>
          <TextInput source="regex" />
          <TextInput source="template" />
        </SimpleFormIterator>
      </ArrayInput>
    </Create>
  );
};

export const ClientInput = (props) => {
  return (
    <MyReferenceInput
      source="clientId"
      reference="clients"
      {...props}
      primaryField="clientId"
    />
  );
};

export const ClientField = (props) => {
  return (
    <MyReferenceField
      source="clientExternalId"
      reference="clients"
      primaryField="clientId"
      {...props}
    />
  );
};
